import axios from 'axios'

import store from '@/store'
import qs from 'qs'
import router from '@/router'
import {isArray} from '@/utils/validate'
import {ElMessage} from 'element-plus'
import { notPopUrlList,defUrlList } from '@/settings'
import {generateDeviceId} from "@/utils/device";
import i18n,{getLocale} from '@/locales/i18n'
import { getAccessToken } from '@/utils/accessToken'  // 导入 i18n 实例
import { isAuthenticated, openLoginDialog } from '@/utils/authGate'

const successCode = [200, 0]
let loadingInstance
let errorStatus = 0

// 必须携带 Authorization 才有意义的接口；无 token 时直接取消请求，避免后端 "Token not provided" 噪音
const AUTH_REQUIRED_PATHS = [
  '/api/socket/get-client-token',
  '/api/socket/get-subscribe-chat-token',
  '/api/socket/get-subscribe-com-chat-token',
  '/api/socket/get-com-chat-channel',
  '/api/socket/report-client-diagnostic'
]

let authResetPromise = null

function getCurrentRoutePath() {
  const currentRoute = router.currentRoute && router.currentRoute.value
  if (!currentRoute || currentRoute.path === '/login') return ''
  return currentRoute.fullPath || currentRoute.path || ''
}

function resetAuthentication(reason, redirect) {
  const target = redirect || getCurrentRoutePath()
  if (!authResetPromise) {
    authResetPromise = store.dispatch('user/resetAll')
      .catch((error) => {
        console.error('重置登录状态失败:', error)
      })
      .finally(() => {
        authResetPromise = null
      })
  }

  openLoginDialog({reason, redirect: target})
}

/**
 * @author errorStatus
 * @description 处理code异常
 * @param {*} code
 * @param {*} message
 */
export const handleCode = (code, message) => {
  const normalizedCode = Number(code)
  switch (normalizedCode) {
    case 401:
      if (errorStatus < 1) {
        errorStatus = 1
        const hadToken = Boolean(store.getters['user/accessToken'] || isAuthenticated())
        if (hadToken) {
          ElMessage.error(i18n.global.t('common.account_logout_need_relogin'))
          resetAuthentication('common.account_logout_need_relogin')
        } else {
          resetAuthentication('')
        }
      }
      break
    case 403:
      ElMessage.error(message || i18n.global.t('common.operation_failed'))
      break
    case 1040:
      if (errorStatus !== 2) {
        ElMessage.error(message || '帐号已在其他地方登录！')
        errorStatus = 2
        resetAuthentication('common.account_logout_need_relogin')
      }
      break
    case 910002:
    case 910003:
      break;
    default:
      ElMessage.error(message || `后端接口${code}异常`)
      break
  }
}

/**
 * @author AgentStore
 * @description axios初始化
 */
const instance = axios.create({
  baseURL: process.env.VUE_APP_BASE_API,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
    'X-Requested-With': 'XMLHttpRequest'
  }
})

/**
 * @author AgentStore
 * @description axios请求拦截器
 */
instance.interceptors.request.use(
  async  (config) => {
    try {
      const deviceId = await generateDeviceId();

      // 为所有请求添加设备ID头
      config.headers['X-Device-ID'] = deviceId
      // 特殊处理登录请求
      if (config.url === '/api/user/login' && config.data) {
        config.data = {
          ...config.data,  // 保留原始数据
          device_id: deviceId
        }
      }
    } catch (e) {
      console.error('设备ID处理异常:', e)
      // 降级处理：设备ID生成失败仍然继续请求
    }
    const accessToken = getAccessToken()
    if (accessToken) {
      config.headers['Authorization'] = 'Bearer ' + accessToken
    } else if (AUTH_REQUIRED_PATHS.some(p => config.url && config.url.indexOf(p) !== -1)) {
      // 无 token 场景下（登出 / cookie 过期 / 跨域前缀错配），ws token 类接口直接取消，避免后端 401 日志
      return Promise.reject(new axios.Cancel('skip: no access token for ' + config.url))
    }
    let language_set= getLocale()
    if(defUrlList.indexOf(config.url)>-1){
      language_set='en-us'
    }
    config.headers['Language-Set'] = language_set
    if (
      config.data &&
      config.headers['Content-Type'] ===
      'application/x-www-form-urlencoded;charset=UTF-8'
    ) {
      config.data = qs.stringify(config.data)
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

/**
 * @author AgentStore
 * @description axios响应拦截器
 */
instance.interceptors.response.use(
  response => {
    if (response.headers && response.headers['authorization']) {
      // 刷新token
      const token = response.headers['authorization'].replace('Bearer ', '')
      const storeToken = store.getters['user/accessToken']
      if (token !== storeToken) {
        store.commit('user/setAccessToken', token)
      }
    }
    // 对于文件下载（blob/arraybuffer），直接返回原始响应，避免按业务 code 解析
    if (response && response.config && (response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer')) {
      return response
    }
    if (loadingInstance) loadingInstance.close()

    const {data, config} = response
    const {code, message} = data
    // 操作正常Code数组
    const codeVerificationArray = isArray(successCode)
      ? [...successCode]
      : [...[successCode]]
    // 是否操作正常
    if (codeVerificationArray.includes(code)) {
      errorStatus = 0
      return data
    } else {
      if ([401, 1040].includes(Number(code))) {
        handleCode(code, message)
      } else if (config.suppressBusinessErrorMessage || config.suppressErrorMessage) {
        console.warn('[request] business error handled by caller:', data)
      } else if(config.url&&notPopUrlList.indexOf(config.url)>-1){
        console.log(data)
      }else{
        handleCode(code, message)
      }
      return Promise.reject({url: config.url, code, message, result: false})
    }
  },
  error => {
    if (loadingInstance) loadingInstance.close()
    // 主动取消的请求（如无 token 时跳过 ws token 类接口），静默忽略
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }
    // Caller-owned messages must not suppress an expired-login reset.
    if (error?.config?.suppressErrorMessage && Number(error?.response?.status) !== 401) {
      return Promise.reject(error)
    }
    const {response, message} = error
    if (error.response && error.response.data) {
      const {status, data} = response
      handleCode(status, data.message || message)
      return Promise.reject(error)
    } else {
      if(error&&error.config&&error.config.url&&notPopUrlList.indexOf(error.config.url)>-1){
        return Promise.reject(error)
      }else{
        let {message} = error
        if (message === 'Network Error') {
          message = i18n.global.t('utils.utils.network_connection_failed')
        }
        if (message.includes('timeout')) {
          message = i18n.global.t('utils.utils.backend_request_timeout')
        }
        if (message.includes('Request failed with status code')) {
          const code = message.substr(message.length - 3)
          message = i18n.global.t('utils.utils.backend_exception_code', {code: code})
        }
        ElMessage.error(message || i18n.global.t('utils.utils.backend_unknown_exception'))
        return Promise.reject(error)
      }
    }
  }
)

export default instance
